import { Link, useParams } from "react-router-dom";
import { CalendarCheck2, ArrowLeft, MessageCircle, Clock } from "lucide-react";
import { PRESTAZIONI, CONTACT, IMAGES, SERVICE_IMAGES } from "@/data/siteData";
import { SERVICE } from "@/constants/testIds";
import SEO from "@/components/SEO";
import { graphSchema, breadcrumbSchema, physicianSchema } from "@/lib/schema";

const SERVICE_LONG = {
  "visita-gastroenterologica": {
    long:
      "La visita gastroenterologica è il primo passo di ogni percorso di cura. In circa 30 minuti raccolgo un'anamnesi accurata, valuto obiettivamente il paziente e definisco un piano diagnostico-terapeutico personalizzato.",
    prep: "Portare con sé documento, tessera sanitaria, referti di esami precedenti e l'elenco dei farmaci in uso.",
    when: "Sintomi digestivi persistenti, controllo di patologie note, secondo parere, follow-up dopo esami endoscopici.",
  },
  gastroscopia: {
    long:
      "La gastroscopia è un esame endoscopico che consente di studiare in modo diretto esofago, stomaco e duodeno. L'esame dura pochi minuti e può essere eseguito con o senza sedazione.",
    prep: "Digiuno da almeno 8 ore. Verranno fornite indicazioni personalizzate sui farmaci da sospendere.",
    when: "Reflusso persistente, dispepsia, dolore epigastrico, anemia sideropenica, difficoltà nella deglutizione, sospetto Helicobacter pylori.",
  },
  colonscopia: {
    long:
      "La colonscopia è lo strumento più efficace per la prevenzione del tumore del colon-retto. Consente diagnosi accurate e rimozione di polipi durante lo stesso esame.",
    prep: "Preparazione intestinale specifica nei giorni precedenti l'esame. Riceverà istruzioni scritte dettagliate.",
    when: "Screening dopo i 50 anni, sangue nelle feci, cambio dell'alvo, calprotectina elevata, familiarità per polipi o neoplasie.",
  },
  "breath-test": {
    long:
      "I test del respiro sono esami non invasivi che permettono di individuare intolleranze al lattosio, malassorbimento del glucosio e sovracrescita batterica del piccolo intestino (SIBO) con il breath test al lattulosio.",
    prep: "Digiuno di 12 ore, dieta specifica nelle 24 ore precedenti. Sospensione di antibiotici e probiotici come da indicazione.",
    when: "Gonfiore addominale, dolore, diarrea, sospetta intolleranza o SIBO.",
  },
  "ecografia-addome": {
    long:
      "L'ecografia dell'addome permette una valutazione morfologica accurata di fegato, colecisti, pancreas, milza e reni. Esame non invasivo e ripetibile.",
    prep: "Digiuno di 6 ore per l'ecografia dell'addome superiore. Vescica piena per lo studio pelvico.",
    when: "Dolore addominale, alterazioni degli esami epatici, sospetta calcolosi biliare, screening del fegato grasso.",
  },
  "ph-impedenziometria": {
    long:
      "La pH-impedenziometria delle 24 ore è l'esame gold standard per la diagnosi oggettiva del reflusso gastroesofageo. Consente di distinguere reflusso acido e non-acido.",
    prep: "Sospensione degli inibitori di pompa protonica (IPP) da 7 giorni, come da indicazione clinica.",
    when: "Reflusso refrattario alla terapia, sintomi extra-esofagei, valutazione pre-chirurgica.",
  },
  "second-opinion": {
    long:
      "Il second opinion è una consulenza specialistica di approfondimento: rivedo diagnosi, referti ed esami già eseguiti per confermare o rivalutare il percorso di cura, offrendo un parere indipendente e basato sull'evidenza.",
    prep: "Portare tutta la documentazione clinica disponibile: referti, esami del sangue, endoscopie, ecografie, terapie in corso.",
    when: "Dubbi su una diagnosi ricevuta, desiderio di un parere aggiuntivo, patologie complesse o terapie che non stanno dando i risultati attesi.",
  },
  "follow-up": {
    long:
      "La visita di follow-up permette di monitorare nel tempo l'andamento delle patologie croniche e l'efficacia delle terapie in corso, adattando il piano di cura all'evoluzione clinica.",
    prep: "Portare i referti degli esami eseguiti dopo l'ultima visita e annotare eventuali nuovi sintomi.",
    when: "Pazienti con patologie croniche (reflusso, IBS, malattie infiammatorie intestinali), controllo dell'efficacia della terapia, rivalutazione periodica.",
  },
  "consulenza-nutrizionale": {
    long:
      "La consulenza dietetico-nutrizionale fornisce indicazioni alimentari personalizzate e basate sull'evidenza per gestire reflusso, colon irritabile, celiachia, intolleranze e altre condizioni digestive.",
    prep: "Utile portare un diario alimentare degli ultimi giorni ed eventuali esami relativi a intolleranze o allergie.",
    when: "Gonfiore, reflusso, colon irritabile, intolleranze alimentari, celiachia, necessità di un piano alimentare mirato.",
  },
  "visita-domiciliare": {
    long:
      "La visita gastroenterologica domiciliare porta la competenza specialistica direttamente a casa del paziente. È pensata per chi ha difficoltà a raggiungere lo studio: pazienti anziani, con ridotta mobilità, fragili o in condizioni che rendono difficile lo spostamento. La visita mantiene lo stesso standard di accuratezza: anamnesi approfondita, esame obiettivo e definizione del piano diagnostico-terapeutico.",
    prep: "Tenere a disposizione documento, tessera sanitaria, referti di esami precedenti e l'elenco dei farmaci in uso. È utile predisporre un ambiente tranquillo per la visita.",
    when: "Pazienti anziani o allettati, ridotta mobilità, condizioni che rendono difficoltoso lo spostamento, necessità di continuità assistenziale a domicilio.",
  },
};

export default function ServicePage() {
  const { slug } = useParams();
  const s = PRESTAZIONI.find((p) => p.slug === slug);
  const detail = SERVICE_LONG[slug];
  const wa = `https://wa.me/39${CONTACT.whatsapp}`;

  if (!s) {
    return (
      <main className="pt-32 pb-24 max-w-3xl mx-auto px-6 text-center">
        <h1 className="font-serif text-4xl text-[#0B4A5A]">
          Prestazione non trovata
        </h1>
        <Link to="/" className="btn-secondary mt-6 inline-flex">
          <ArrowLeft size={16} /> Home
        </Link>
      </main>
    );
  }

  return (
    <main data-testid={SERVICE.root(slug)} className="pt-28">
      <SEO
        title={`${s.name} — Gastroenterologo Sardegna`}
        description={s.desc}
        path={`/prestazioni/${slug}`}
        jsonLd={graphSchema([
          {
            "@context": "https://schema.org",
            "@type": "MedicalProcedure",
            name: s.name,
            description: s.desc,
          },
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Prestazioni", path: "/#prestazioni" },
            { name: s.name, path: `/prestazioni/${slug}` },
          ]),
          physicianSchema(),
        ])}
      />
      <section className="relative py-16 md:py-24 bg-[#F9FAFB]">
        <div className="max-w-6xl mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <div>
            <Link
              to="/#prestazioni"
              className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-[#0B4A5A] transition"
            >
              <ArrowLeft size={14} /> Tutte le prestazioni
            </Link>
            <p className="overline mt-8">Prestazione</p>
            <h1 className="font-serif text-4xl md:text-6xl mt-4 text-[#0B4A5A] leading-[1.05]">
              {s.name}
            </h1>
            <div className="mt-6 inline-flex items-center gap-2 text-sm text-gray-600 border border-gray-200 rounded-full px-4 py-2 bg-white">
              <Clock size={14} className="text-[#C6A86B]" />
              {s.duration}
            </div>
            <p className="mt-6 text-lg text-gray-700 leading-relaxed">
              {s.desc}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                data-testid={SERVICE.ctaBook}
                href={CONTACT.miodottore}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary"
              >
                <CalendarCheck2 size={16} /> Prenota
              </a>
              <a
                href={wa}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary"
              >
                <MessageCircle size={16} /> Info WhatsApp
              </a>
            </div>
          </div>
          <div className="rounded-2xl overflow-hidden shadow-[0_20px_60px_rgba(11,74,90,0.12)]">
            <img
              src={SERVICE_IMAGES[slug] || IMAGES.consultation}
              alt={s.name}
              className="w-full h-[420px] object-cover"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {detail && (
        <section className="py-16 md:py-24 bg-white">
          <div className="max-w-3xl mx-auto px-6 md:px-10 space-y-12">
            <div>
              <h2 className="font-serif text-3xl text-[#0B4A5A]">In cosa consiste</h2>
              <div className="gold-line mt-4 w-12" />
              <p className="mt-5 text-gray-700 leading-[1.8]">{detail.long}</p>
            </div>
            <div>
              <h2 className="font-serif text-3xl text-[#0B4A5A]">Quando è indicata</h2>
              <div className="gold-line mt-4 w-12" />
              <p className="mt-5 text-gray-700 leading-[1.8]">{detail.when}</p>
            </div>
            <div>
              <h2 className="font-serif text-3xl text-[#0B4A5A]">Preparazione</h2>
              <div className="gold-line mt-4 w-12" />
              <p className="mt-5 text-gray-700 leading-[1.8]">{detail.prep}</p>
            </div>
          </div>
        </section>
      )}
    </main>
  );
}
